import { Icon } from '@iconify/react'
import { FormEvent, useState } from 'react'
import { useAuth } from '../components/AuthContext'
import { Link } from 'react-router-dom'

type Phase = 'form' | 'result'

export function RegisterPage() {
  const { register, login } = useAuth()
  const [userId, setUserId] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [phase, setPhase] = useState<Phase>('form')
  const [copied, setCopied] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!userId.trim()) {
      setError('User ID is required.')
      return
    }
    setBusy(true)
    setError('')

    try {
      const generated = await register(userId.trim())
      setPassword(generated)
      setPhase('result')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setBusy(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(password).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleContinue = async () => {
    setBusy(true)
    try {
      await login(userId.trim(), password)
    } catch {
      // Let them copy the password and try manually
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm">
        <div className="panel">
          <div className="mb-6 flex items-center gap-3">
            <Icon icon="mdi:account-plus" width="28" height="28" className="text-cyan-400" />
            <h1 className="text-xl font-semibold text-white">Register</h1>
          </div>

          {phase === 'form' && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5 text-sm text-slate-300">
                Choose a User ID
                <input
                  className="field"
                  type="text"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  placeholder="e.g. boby"
                  autoFocus
                />
              </label>

              {error && (
                <p className="text-sm text-rose-300">
                  <Icon icon="mdi:alert-circle" className="mr-1 inline" width="14" />
                  {error}
                </p>
              )}

              <button className="btn flex items-center justify-center gap-2" disabled={busy}>
                {busy && <Icon icon="mdi:loading" width="16" className="animate-spin" />}
                {busy ? 'Creating...' : 'Create account'}
              </button>

              <p className="text-center text-xs text-slate-400">
                Already have an account?{' '}
                <Link to="/login" className="text-cyan-400 underline hover:text-cyan-300">
                  Log in
                </Link>
              </p>
            </form>
          )}

          {phase === 'result' && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border border-amber-400/40 bg-amber-900/20 p-4">
                <p className="mb-2 flex items-center gap-1 text-sm font-semibold text-amber-200">
                  <Icon icon="mdi:alert" width="16" />
                  Save this password — shown once
                </p>
                <p className="mb-3 text-xs text-amber-300/80">
                  This is your generated phrase password. You will need it to log in. It cannot be
                  recovered if lost.
                </p>

                <div className="relative">
                  <code className="field block break-all pr-10 font-mono text-sm text-amber-100">
                    {password}
                  </code>
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    onClick={handleCopy}
                    title="Copy password"
                  >
                    <Icon
                      icon={copied ? 'mdi:check' : 'mdi:content-copy'}
                      width="18"
                    />
                  </button>
                </div>
              </div>

              <button
                className="btn flex items-center justify-center gap-2"
                onClick={handleContinue}
                disabled={busy}
              >
                {busy && <Icon icon="mdi:loading" width="16" className="animate-spin" />}
                {busy ? 'Logging in...' : 'Copy & continue'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
